import { useEffect, useRef, useState } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import { isRecaptchaSdkAvailable } from "./recaptchaUtils";
import { captureRecaptchaException, captureRecaptchaMessage } from "../../infra/sentry";

const useRecaptcha = ({
  visible = false,
  containerId,
  onRecaptchaRenderFailed,
  onSolvedRecaptcha,
  onRejectedRecaptcha,
}) => {
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);
  const [isRecaptchaReady, setIsRecaptchaReady] = useState(false);
  const [isRecaptchaLoading, setIsRecaptchaLoading] = useState(true);
  const [hasRecaptchaLoadFailed, setHasRecaptchaLoadFailed] = useState(false);
  const [isRecaptchaVerified, setIsRecaptchaVerified] = useState(false);
  const verifierRef = useRef(null);
  const callbacksRef = useRef({ onRecaptchaRenderFailed, onSolvedRecaptcha, onRejectedRecaptcha });
  callbacksRef.current = { onRecaptchaRenderFailed, onSolvedRecaptcha, onRejectedRecaptcha };

  const initRecaptcha = async () => {
    setIsRecaptchaLoading(true);
    setHasRecaptchaLoadFailed(false);
    setIsRecaptchaReady(false);
    setIsRecaptchaVerified(false);
    setRecaptchaVerifier(null);

    const containerElement = document.getElementById(containerId);
    if (!containerElement) {
      setIsRecaptchaLoading(false);
      setHasRecaptchaLoadFailed(true);
      captureRecaptchaMessage("reCAPTCHA container not found", "error", {
        containerId,
        visible,
        phase: "pre-init",
      });
      callbacksRef.current.onRecaptchaRenderFailed && callbacksRef.current.onRecaptchaRenderFailed();
      return;
    }

    if (verifierRef.current) {
      try {
        verifierRef.current.clear();
      } catch (e) {
        captureRecaptchaMessage("reCAPTCHA cleanup failed", "warning", {
          containerId,
          visible,
          phase: "cleanup",
        });
      }
      verifierRef.current = null;
    }

    if (!isRecaptchaSdkAvailable()) {
      setIsRecaptchaLoading(false);
      setHasRecaptchaLoadFailed(true);
      captureRecaptchaMessage("reCAPTCHA SDK unavailable", "error", {
        containerId,
        visible,
        phase: "pre-init",
        grecaptchaDefined: typeof window.grecaptcha !== "undefined",
        recaptchaVerifierDefined: false,
      });
      callbacksRef.current.onRecaptchaRenderFailed && callbacksRef.current.onRecaptchaRenderFailed();
      return;
    }

    try {
      const verifier = new firebase.auth.RecaptchaVerifier(containerId, {
        size: visible ? "normal" : "invisible",
        callback: () => {
          setIsRecaptchaVerified(true);
          callbacksRef.current.onSolvedRecaptcha && callbacksRef.current.onSolvedRecaptcha();
        },
        "expired-callback": () => {
          setIsRecaptchaVerified(false);
          callbacksRef.current.onRejectedRecaptcha && callbacksRef.current.onRejectedRecaptcha();

          captureRecaptchaMessage("reCAPTCHA token expired", "warning", {
            containerId,
            phase: "expired-callback",
          });

          try {
            verifier.clear();
          } catch (clearError) {
            captureRecaptchaException(clearError, {
              containerId,
              phase: "expired-clear",
            });
          }

          verifier
            .render()
            .then((widgetId) => {
              window.recaptchaWidgetId = widgetId;
              if (typeof window.grecaptcha !== "undefined") {
                window.grecaptcha.reset(widgetId);
              }
              setIsRecaptchaReady(true);
            })
            .catch((renderError) => {
              captureRecaptchaException(renderError, {
                containerId,
                phase: "expired-rerender",
              });
              initRecaptcha();
            });
        },
      });

      window.recaptchaWidgetId = await verifier.render();

      if (typeof window.grecaptcha === "undefined") {
        setHasRecaptchaLoadFailed(true);
        captureRecaptchaMessage("reCAPTCHA blocked or not loaded", "warning", {
          containerId,
          visible,
          phase: "post-render",
        });
        callbacksRef.current.onRecaptchaRenderFailed && callbacksRef.current.onRecaptchaRenderFailed();
        return;
      }

      verifierRef.current = verifier;
      setRecaptchaVerifier(verifier);
      setIsRecaptchaReady(true);
    } catch (e) {
      captureRecaptchaException(e, {
        containerId,
        visible,
        phase: "init",
      });
      setHasRecaptchaLoadFailed(true);
      callbacksRef.current.onRecaptchaRenderFailed && callbacksRef.current.onRecaptchaRenderFailed();
    } finally {
      setIsRecaptchaLoading(false);
    }
  };

  const resetRecaptchaWidget = async () => {
    const verifier = verifierRef.current;
    if (!verifier) {
      return;
    }

    try {
      const widgetId = await verifier.render();
      window.recaptchaWidgetId = widgetId;
      if (typeof window.grecaptcha !== "undefined") {
        window.grecaptcha.reset(widgetId);
      }
    } catch (e) {
      captureRecaptchaException(e, {
        containerId,
        phase: "widget-reset",
      });
      await initRecaptcha();
    }
  };

  useEffect(() => {
    initRecaptcha();

    return () => {
      if (verifierRef.current) {
        try {
          verifierRef.current.clear();
        } catch (e) {
          captureRecaptchaMessage("reCAPTCHA cleanup failed", "warning", {
            containerId,
            phase: "unmount-cleanup",
          });
        }
        verifierRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerId, visible]);

  return {
    recaptchaVerifier,
    isRecaptchaReady,
    isRecaptchaLoading,
    hasRecaptchaLoadFailed,
    isRecaptchaVerified,
    setIsRecaptchaVerified,
    resetRecaptcha: initRecaptcha,
    resetRecaptchaWidget,
  };
};

export default useRecaptcha;
